import { useEffect } from "react";
import { CONTACT } from "@/config/contact";

/**
 * Injects/updates SEO metadata & JSON-LD structured data per page.
 *
 * Props:
 *  - title, description, canonical, image
 *  - jsonLd: object or array of JSON-LD schema objects
 */
export default function SEO({ title, description, canonical, image, jsonLd }) {
  useEffect(() => {
    const fullTitle = title ? `${title} | casa no porto` : "casa no porto — Zélia Silva | Consultora Imobiliária no Porto";
    document.title = fullTitle;

    const setMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    setMeta("name", "description", description);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "casa no porto");
    setMeta("property", "og:image", image || CONTACT.photo);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image || CONTACT.photo);

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    // Remove old JSON-LD injected by us
    document.querySelectorAll('script[data-cnp-jsonld="true"]').forEach((s) => s.remove());
    const items = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    items.forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-cnp-jsonld", "true");
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });

    return () => {
      document.querySelectorAll('script[data-cnp-jsonld="true"]').forEach((s) => s.remove());
    };
  }, [title, description, canonical, image, JSON.stringify(jsonLd)]);

  return null;
}

export function realEstateAgentSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["RealEstateAgent", "LocalBusiness"],
    "@id": "https://casanoporto.com/#agent",
    name: "casa no porto — Zélia Silva",
    alternateName: "Zélia Silva Consultora Imobiliária",
    description:
      "Consultora imobiliária no Porto e Grande Porto. Especialista em Porto, Gondomar (Rio Tinto), Maia, Ermesinde e Valongo. Mais de 10 anos de experiência. Mediação por eXp Global Portugal, Lda. — AMI 18470.",
    url: "https://casanoporto.com/",
    image: CONTACT.photo,
    logo: "https://casanoporto.com/logo.png",
    telephone: CONTACT.phone,
    email: CONTACT.email,
    priceRange: "€€€",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Porto",
      addressRegion: "Porto",
      addressCountry: "PT",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.1579,
      longitude: -8.6291,
    },
    areaServed: [
      { "@type": "City", name: "Porto" },
      { "@type": "City", name: "Gondomar" },
      { "@type": "City", name: "Maia" },
      { "@type": "City", name: "Ermesinde" },
      { "@type": "City", name: "Valongo" },
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "19:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Saturday"],
        opens: "10:00",
        closes: "18:00",
      },
    ],
    sameAs: [CONTACT.instagram, CONTACT.facebook],
    parentOrganization: { "@type": "Organization", name: "eXp Portugal" },
    knowsLanguage: ["pt-PT", "en"],
    slogan: "Encontre a casa dos seus sonhos e transforme-a no seu lar.",
    identifier: {
      "@type": "PropertyValue",
      propertyID: "AMI",
      value: "18470",
    },
  };
}

export function propertySchema(p, canonical) {
  const s = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.title,
    description: p.short_description || p.full_description,
    image: p.gallery && p.gallery.length ? p.gallery : [p.main_image],
    url: canonical,
    brand: { "@type": "Organization", name: "casa no porto — Zélia Silva" },
    offers: {
      "@type": "Offer",
      price: p.price,
      priceCurrency: p.currency || "EUR",
      availability: p.status === "vendido" ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
      url: canonical,
      seller: { "@id": "https://casanoporto.com/#agent" },
    },
    additionalProperty: [
      { "@type": "PropertyValue", name: "Tipologia", value: p.typology },
      { "@type": "PropertyValue", name: "Área", value: p.area, unitCode: "MTK" },
      { "@type": "PropertyValue", name: "Tipo", value: p.property_type },
    ],
  };
  if (p.locality) {
    s.address = {
      "@type": "PostalAddress",
      addressLocality: p.locality,
      addressRegion: "Porto",
      addressCountry: "PT",
    };
  }
  if (p.latitude != null && p.longitude != null) {
    s.geo = { "@type": "GeoCoordinates", latitude: p.latitude, longitude: p.longitude };
  }
  return s;
}

export function faqPageSchema(items) {
  if (!items || items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.question,
      acceptedAnswer: { "@type": "Answer", text: it.answer },
    })),
  };
}

export function articleSchema(b, canonical) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: b.title,
    description: b.summary || b.meta_description,
    image: b.main_image,
    datePublished: b.date,
    author: { "@type": "Person", name: b.author || "Zélia Silva" },
    publisher: { "@type": "Organization", name: "casa no porto" },
    mainEntityOfPage: canonical,
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}
